import { useState, useEffect } from 'react'
import { Box, Grid, Card, CardContent, Typography, Button, Stack, Chip } from '@mui/material'
import { Visibility as ViewIcon } from '@mui/icons-material'
import { useGetDataModelSchema } from 'pages/DataModelSchema/api'
import { DataModelSchemaDetail } from 'common/types/kraken-core/DataModelSchema'
import { TranslationMap } from 'common/types/kraken-core/TranslationMap'
import DataModelSchemaStructureDrawer from './mapping/DataModelSchemaStructureDrawer'
import ContentTypeTag from 'atoms/ContentTypeTag/ContentTypeTag'

export interface ImportModalConfigurationCardsProps {
  translationMap: TranslationMap
  viewMode: 'simple' | 'advanced'
  onUpdate: (translationMap: TranslationMap) => void
}

export default function ImportModalConfigurationCards({
  translationMap,
  viewMode,
  onUpdate,
}: ImportModalConfigurationCardsProps) {
  // State for data model details
  const [inputDataModel, setInputDataModel] = useState<DataModelSchemaDetail>()
  const [outputDataModel, setOutputDataModel] = useState<DataModelSchemaDetail>()

  // State for data model structure dialogs
  const [inputDataModelStructureDialogOpen, setInputDataModelStructureDialogOpen] = useState(false)
  const [outputDataModelStructureDialogOpen, setOutputDataModelStructureDialogOpen] = useState(
    false
  )

  // API hooks to fetch data model details
  const { data: inputDataModelData, isLoading: isLoadingInputDataModel } = useGetDataModelSchema(
    translationMap?.input_data_model || '',
    {
      enabled: Boolean(translationMap?.input_data_model),
    }
  )

  const { data: outputDataModelData, isLoading: isLoadingOutputDataModel } = useGetDataModelSchema(
    translationMap?.output_data_model || '',
    {
      enabled: Boolean(translationMap?.output_data_model),
    }
  )

  // Update data model states when data is fetched
  useEffect(() => {
    if (inputDataModelData) {
      setInputDataModel(inputDataModelData)
    }
  }, [inputDataModelData])

  useEffect(() => {
    if (outputDataModelData) {
      setOutputDataModel(outputDataModelData)
    }
  }, [outputDataModelData])

  // Clear data model states when IDs are cleared
  useEffect(() => {
    if (!translationMap?.input_data_model) {
      setInputDataModel(undefined)
    }
  }, [translationMap?.input_data_model])

  useEffect(() => {
    if (!translationMap?.output_data_model) {
      setOutputDataModel(undefined)
    }
  }, [translationMap?.output_data_model])

  // Data model structure dialog handlers
  const handleOpenInputDataModelStructureDialog = () => setInputDataModelStructureDialogOpen(true)
  const handleCloseInputDataModelStructureDialog = () => setInputDataModelStructureDialogOpen(false)

  const handleOpenOutputDataModelStructureDialog = () => setOutputDataModelStructureDialogOpen(true)
  const handleCloseOutputDataModelStructureDialog = () =>
    setOutputDataModelStructureDialogOpen(false)

  // Handler for ConfigurationCard updates
  const handleConfigurationUpdate = (
    dataModel: DataModelSchemaDetail,
    field: string,
    value: any
  ) => {
    const updatedTranslationMap = { ...translationMap, [field]: value }
    onUpdate(updatedTranslationMap)
  }

  // Determine if we're using data models or content types
  const isUsingDataModels = Boolean(
    translationMap?.input_data_model || translationMap?.output_data_model
  )
  const isUsingContentTypes = Boolean(
    translationMap?.input_content_type || translationMap?.output_content_type
  )

  return (
    <Box>
      {/* Translation Map Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Translation Map Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{translationMap?.name || 'Not specified'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Direction
              </Typography>
              <Chip
                label={translationMap?.use_when_direction || 'Not specified'}
                color={translationMap?.use_when_direction === 'inbound' ? 'primary' : 'secondary'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Input Configuration Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Input Configuration
              </Typography>

              {isUsingDataModels && translationMap?.input_data_model ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data Model
                    </Typography>
                    <Typography variant="body1">
                      {inputDataModel?.description || 'Loading...'}
                    </Typography>
                  </Box>
                  {inputDataModel && (
                    <Button
                      startIcon={<ViewIcon />}
                      onClick={handleOpenInputDataModelStructureDialog}
                      size="small"
                      variant="outlined"
                    >
                      View Structure
                    </Button>
                  )}
                </Stack>
              ) : isUsingContentTypes && translationMap?.input_content_type ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Content Type
                  </Typography>
                  <ContentTypeTag contentType={translationMap.input_content_type} size="default" />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No input configuration specified
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Output Configuration Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Output Configuration
              </Typography>

              {isUsingDataModels && translationMap?.output_data_model ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data Model
                    </Typography>
                    <Typography variant="body1">
                      {outputDataModel?.description || 'Loading...'}
                    </Typography>
                  </Box>
                  {outputDataModel && (
                    <Button
                      startIcon={<ViewIcon />}
                      onClick={handleOpenOutputDataModelStructureDialog}
                      size="small"
                      variant="outlined"
                    >
                      View Structure
                    </Button>
                  )}
                </Stack>
              ) : isUsingContentTypes && translationMap?.output_content_type ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Content Type
                  </Typography>
                  <ContentTypeTag contentType={translationMap.output_content_type} size="default" />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No output configuration specified
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Model Structure Drawers */}
      <DataModelSchemaStructureDrawer
        open={inputDataModelStructureDialogOpen}
        onClose={handleCloseInputDataModelStructureDialog}
        dataModel={inputDataModel}
        title="Input Data Model Schema Structure"
      />
      <DataModelSchemaStructureDrawer
        open={outputDataModelStructureDialogOpen}
        onClose={handleCloseOutputDataModelStructureDialog}
        dataModel={outputDataModel}
        title="Output Data Model Schema Structure"
      />
    </Box>
  )
}
